import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import { useHistory, useParams } from "react-router-dom";
import UploadBox from "../../../../shared/molecules/fields/UploadBox";
import autoGradeClient from "../../../../utils/graphql/autoGradeClient";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import {
  GET_ALL_EXAM_PAPERS,
  GET_ALL_EXAM_PAPERS_ANSWER_KEY,
  GET_PRESIGNED_URL,
} from "../../../../utils/graphql/services/ai-evaluation/queries";
import { UPDATE_EXAM_PAPER_ANSWER_KEY } from "../../../../utils/graphql/services/ai-evaluation/mutations";

type AnswerKeyForm = {
  id: number;
  examPaperId: number | "";
  answerKeyPublicPdfUrl: string;
  isArchived: boolean;
  mode: string;
};

type ExamPaper = { id: string | number; title: string };

const DEFAULT_FORM: AnswerKeyForm = {
  id: 0,
  examPaperId: "",
  answerKeyPublicPdfUrl: "",
  isArchived: false,
  mode: "",
};

function UpdateExamAnswerKeyPage() {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const [form, setForm] = useState<AnswerKeyForm>(DEFAULT_FORM);
  const [originalForm, setOriginalForm] = useState<AnswerKeyForm>(DEFAULT_FORM);
  const [uploadedFileName, setUploadedFileName] = useState<string>("");
  const [searchValue, setSearchValue] = useState<string>("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>("");
  const [examPaperOptions, setExamPaperOptions] = useState<ExamPaper[]>([]);
  const [selectedExamPaper, setSelectedExamPaper] = useState<ExamPaper | null>(
    null
  );

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const hasLoadedSelectedPaper = useRef(false);
  const isInitialMount = useRef(true);

  const handleSnackbarClose = useCallback(() => {
    setOpenSnackbar(false);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchValue]);

  const { data: answerKeyData, loading: loadingAnswerKey } = useQuery(
    GET_ALL_EXAM_PAPERS_ANSWER_KEY,
    {
      client: autoGradeClient,
      variables: { input: { id: Number(id) } },
      fetchPolicy: "network-only",
    }
  );

  const [searchExamPapers, { loading: loadingExamPapers, data: searchData }] =
    useLazyQuery(GET_ALL_EXAM_PAPERS, {
      client: autoGradeClient,
      fetchPolicy: "network-only",
    });

  const [fetchSelectedPaper, { data: selectedPaperData }] = useLazyQuery(
    GET_ALL_EXAM_PAPERS,
    {
      client: autoGradeClient,
      fetchPolicy: "network-only",
    }
  );

  useEffect(() => {
    if (answerKeyData?.getExamPaperAnswerKeys?.examPaperAnswerKeys?.length) {
      const keyData =
        answerKeyData.getExamPaperAnswerKeys.examPaperAnswerKeys[0];
      const initialForm: AnswerKeyForm = {
        id: keyData.id,
        examPaperId: keyData.examPaperId || "",
        answerKeyPublicPdfUrl: keyData.answerKeyPublicPdfUrl || "",
        isArchived: keyData.isArchived || false,
        mode: keyData.mode || "",
      };
      setForm(initialForm);
      setOriginalForm(initialForm);
      setUploadedFileName(
        initialForm.answerKeyPublicPdfUrl.split("/").pop() || ""
      );

      if (initialForm.examPaperId && !hasLoadedSelectedPaper.current) {
        hasLoadedSelectedPaper.current = true;
        fetchSelectedPaper({
          variables: {
            input: {
              limit: 1,
              offset: 0,
              id: initialForm.examPaperId,
            },
          },
        });
      }
    }
  }, [answerKeyData, fetchSelectedPaper]);

  useEffect(() => {
    if (selectedPaperData?.getExamPapers?.examPapers?.length) {
      const paper = selectedPaperData.getExamPapers.examPapers[0];
      setSelectedExamPaper(paper);
      setExamPaperOptions([paper]);
    }
  }, [selectedPaperData]);

  useEffect(() => {
    if (searchData?.getExamPapers?.examPapers) {
      const newOptions = searchData.getExamPapers.examPapers;

      if (selectedExamPaper) {
        const selectedExists = newOptions.some(
          (opt: ExamPaper) => opt.id === selectedExamPaper.id
        );
        if (!selectedExists) {
          setExamPaperOptions([selectedExamPaper, ...newOptions]);
        } else {
          setExamPaperOptions(newOptions);
        }
      } else {
        setExamPaperOptions(newOptions);
      }
    }
  }, [searchData, selectedExamPaper]);

  // Trigger search when debounced value changes (but not on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    searchExamPapers({
      variables: {
        input: {
          limit: 10,
          offset: 0,
          title: debouncedSearchValue || undefined,
          createdFrom: null,
          createdTill: null,
          updatedFrom: null,
          updatedTill: null,
        },
      },
    });
  }, [debouncedSearchValue, searchExamPapers]);

  const [updateAnswerKey, { loading: updating }] = useMutation(
    UPDATE_EXAM_PAPER_ANSWER_KEY,
    { client: autoGradeClient }
  );

  const handleSearchChange = useCallback(
    (_event: any, newInputValue: string) => {
      setSearchValue(newInputValue);
    },
    []
  );

  const handleChange = useCallback((field: keyof AnswerKeyForm, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleExamPaperChange = useCallback(
    (_event: any, newValue: ExamPaper | null) => {
      setSelectedExamPaper(newValue);
      handleChange("examPaperId", newValue ? newValue.id : "");
    },
    [handleChange]
  );

  const isValid = useMemo(() => {
    return (
      form.examPaperId !== "" &&
      form.answerKeyPublicPdfUrl.trim() !== "" &&
      form.mode.trim() !== ""
    );
  }, [form.examPaperId, form.answerKeyPublicPdfUrl, form.mode]);

  const isModified = useMemo(() => {
    return (
      form.examPaperId !== originalForm.examPaperId ||
      form.answerKeyPublicPdfUrl !== originalForm.answerKeyPublicPdfUrl ||
      form.isArchived !== originalForm.isArchived ||
      form.mode !== originalForm.mode
    );
  }, [form, originalForm]);

  const handleSave = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!form.examPaperId) {
        setErrorMessage("Please select an exam paper");
        setOpenSnackbar(true);
        return;
      }

      if (!form.answerKeyPublicPdfUrl.trim()) {
        setErrorMessage("Please upload an answer key PDF");
        setOpenSnackbar(true);
        return;
      }

      if (!form.mode.trim()) {
        setErrorMessage("Please select a grading mode");
        setOpenSnackbar(true);
        return;
      }

      try {
        // Build input object with only modified fields
        const updateInput: any = {
          id: form.id, // ID is always required
        };

        // Only add fields that have been modified
        if (form.examPaperId !== originalForm.examPaperId) {
          updateInput.examPaperId = form.examPaperId || null;
        }
        if (form.answerKeyPublicPdfUrl !== originalForm.answerKeyPublicPdfUrl) {
          updateInput.answerKeyPublicPdfUrl =
            form.answerKeyPublicPdfUrl || null;
        }
        if (form.isArchived !== originalForm.isArchived) {
          updateInput.isArchived = form.isArchived;
        }
        if (form.mode !== originalForm.mode) {
          updateInput.mode = form.mode || null;
        }

        await updateAnswerKey({
          variables: {
            input: updateInput,
          },
        });
        setSuccessMessage("Answer key updated successfully!");
        setErrorMessage("");
        setOpenSnackbar(true);

        setTimeout(() => {
          history.push("/ai-evaluation/answer-key");
        }, 2000);
      } catch (error: any) {
        console.error("Failed to update answer key:", error);
        setErrorMessage(
          error.message || "Failed to update answer key. Please try again."
        );
        setSuccessMessage("");
        setOpenSnackbar(true);
      }
    },
    [form, originalForm, updateAnswerKey, history]
  );

  const handleCancel = useCallback(() => {
    history.goBack();
  }, [history]);

  if (loadingAnswerKey || (form.examPaperId && !selectedExamPaper)) {
    return (
      <Grid container spacing={3} sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" align="center">
              Loading answer key data...
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={3} sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      <Grid item xs={12}>
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <Box mb={3}>
            <Typography
              variant="h5"
              component="h1"
              gutterBottom
              sx={{ fontWeight: 600, color: "primary.main" }}
            >
              Update Answer Key #{id}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Modify the answer key configuration and upload settings
            </Typography>
          </Box>

          <Box component="form" onSubmit={handleSave}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  value={selectedExamPaper}
                  onChange={handleExamPaperChange}
                  onInputChange={handleSearchChange}
                  inputValue={searchValue}
                  options={examPaperOptions}
                  getOptionLabel={(option) => option?.title || ""}
                  isOptionEqualToValue={(option, value) =>
                    option?.id === value?.id
                  }
                  loading={loadingExamPapers}
                  loadingText="Searching exam papers..."
                  noOptionsText={
                    loadingExamPapers ? "Searching..." : "No exam papers found"
                  }
                  filterOptions={(options) => options}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Exam Paper"
                      placeholder="Search exam papers by title..."
                      required
                      error={!form.examPaperId && Boolean(errorMessage)}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.id}>
                      <Typography variant="body1">{option.title}</Typography>
                    </Box>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  required
                  error={!form.mode && Boolean(errorMessage)}
                >
                  <InputLabel id="mode-select-label">Grading Mode</InputLabel>
                  <Select
                    labelId="mode-select-label"
                    value={form.mode || ""}
                    onChange={(e) => handleChange("mode", e.target.value)}
                    label="Grading Mode"
                  >
                    <MenuItem value="Standard">Standard</MenuItem>
                    <MenuItem value="Lenient">Lenient</MenuItem>
                    <MenuItem value="Strict">Strict</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="archived-select-label">
                    Archive Status
                  </InputLabel>
                  <Select
                    labelId="archived-select-label"
                    value={form.isArchived ? "true" : "false"}
                    onChange={(e) =>
                      handleChange("isArchived", e.target.value === "true")
                    }
                    label="Archive Status"
                  >
                    <MenuItem value="false">Active</MenuItem>
                    <MenuItem value="true">Archived</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="h6"
                  component="h2"
                  gutterBottom
                  sx={{ mt: 2, mb: 2, fontWeight: 500 }}
                >
                  Answer Key Document
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={8}>
                    <Box sx={{ mb: 2 }}>
                      <UploadBox
                        setUserAnswerAsPdf={(url: string) =>
                          handleChange("answerKeyPublicPdfUrl", url)
                        }
                        filename={uploadedFileName}
                        setFilename={setUploadedFileName}
                        usecase="answersheet_upload"
                        acceptedFormats={["pdf"]}
                        presignedUrlQuery={GET_PRESIGNED_URL}
                        client={autoGradeClient}
                        placeholderText="Click to choose answer key PDF"
                      />
                    </Box>
                    {form.answerKeyPublicPdfUrl && (
                      <TextField
                        fullWidth
                        label="Answer Key PDF URL"
                        value={form.answerKeyPublicPdfUrl}
                        onChange={(e) =>
                          handleChange("answerKeyPublicPdfUrl", e.target.value)
                        }
                        placeholder="PDF URL will appear here after upload"
                        helperText="Current answer key document URL"
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: "grey.50",
                        borderRadius: 1,
                        height: "fit-content",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        gutterBottom
                        sx={{ fontWeight: 600 }}
                      >
                        Update Guidelines
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        component="ul"
                        sx={{ pl: 0, listStyle: "none" }}
                      >
                        <li>• Only PDF files are accepted</li>
                        <li>• Maximum file size: 10MB</li>
                        <li>• Upload a new file to replace current</li>
                        <li>• Changes require manual save</li>
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Box
                  display="flex"
                  justifyContent="flex-end"
                  gap={2}
                  sx={{
                    mt: 3,
                    pt: 2,
                    borderTop: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    size="large"
                    sx={{ minWidth: 120 }}
                    disabled={updating}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={!isModified || updating || !isValid}
                    size="large"
                    sx={{ minWidth: 120 }}
                  >
                    {updating ? "Updating..." : "Update Answer Key"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Grid>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {successMessage ? (
          <Alert
            onClose={handleSnackbarClose}
            severity="success"
            variant="filled"
            sx={{
              width: "100%",
              minWidth: "300px",
              "& .MuiAlert-message": {
                fontSize: "0.875rem",
                fontWeight: 500,
              },
            }}
          >
            {successMessage}
          </Alert>
        ) : (
          <Alert
            onClose={handleSnackbarClose}
            severity="error"
            variant="filled"
            sx={{
              width: "100%",
              minWidth: "300px",
              "& .MuiAlert-message": {
                fontSize: "0.875rem",
                fontWeight: 500,
              },
            }}
          >
            {errorMessage}
          </Alert>
        )}
      </Snackbar>
    </Grid>
  );
}

export default UpdateExamAnswerKeyPage;
